import type { EstimateEasyPrzedmiarRowDto } from '../services/estimateService';

/**
 * Statystyka kolumny „Pozycje” na liście kosztorysów oferty.
 * - `withCenaJedn` — pozycje z ceną jedn. &gt; 0
 * - `total` — wszystkie pozycje w kosztorysie
 * - `uniqueTotal` — liczba unikalnych pozycji po „Podstawa” + „Opis”
 *   (tylko tam, gdzie to da się policzyć; widok listy kosztorysów pomija)
 */
export type PozycjeKosztorysStats = {
  withCenaJedn: number;
  total: number;
  uniqueTotal?: number;
};

/** Jak w EasyEstimatePrzedmiarTab — parsowanie liczb PL do obliczeń wartości netto. */
function parseLoosePlDecimal(raw: string): number | null {
  const t = raw.trim().replace(/\u00a0/g, '').replace(/\s/g, '');
  if (!t) return null;
  const lastComma = t.lastIndexOf(',');
  const lastDot = t.lastIndexOf('.');
  let normalized: string;
  if (lastComma === -1 && lastDot === -1) {
    normalized = t;
  } else if (lastComma > lastDot) {
    const intPart = t.slice(0, lastComma).replace(/\./g, '');
    const fracPart = t.slice(lastComma + 1).replace(/,/g, '');
    normalized = `${intPart}.${fracPart}`;
  } else {
    normalized = t.replace(/,/g, '');
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Suma wartości netto pozycji (ilość × cena jedn., z uwzględnieniem cellPatch z extras)
 * — zgodnie z logiką tabeli Kosztorys / przedmiar.
 */
/** Liczba wierszy typu „pozycja” (bez działów) — zgodnie z logiką sumy netto z przedmiaru. */
export function countPozycjeRowsInEasyPrzedmiar(rows: EstimateEasyPrzedmiarRowDto[]): number {
  let n = 0;
  for (const r of rows) {
    const rt = String(r.rowType ?? r.row_type ?? '').toLowerCase();
    if (rt === 'pozycja') n++;
  }
  return n;
}

function cenaJednRawFromPozycjaRow(r: EstimateEasyPrzedmiarRowDto): string {
  const ex = (r.extras && typeof r.extras === 'object' ? r.extras : {}) as Record<string, unknown>;
  let cenaRaw = ex.cenaJedn != null ? String(ex.cenaJedn).trim() : '';
  const patch = ex.cellPatch;
  if (patch && typeof patch === 'object') {
    const p = patch as Record<string, unknown>;
    if (typeof p.cenaJedn === 'string') cenaRaw = p.cenaJedn.trim();
  }
  return cenaRaw;
}

/** Pozycje przedmiaru z ceną jedn. &gt; 0 (parsowalną); 0 lub pusto — bez ceny (jak brak ceny jedn.). */
export function countPozycjeWithCenaJednInEasyPrzedmiar(rows: EstimateEasyPrzedmiarRowDto[]): number {
  let n = 0;
  for (const r of rows) {
    const rt = String(r.rowType ?? r.row_type ?? '').toLowerCase();
    if (rt !== 'pozycja') continue;
    const cenaRaw = cenaJednRawFromPozycjaRow(r);
    if (!cenaRaw) continue;
    const v = parseLoosePlDecimal(cenaRaw);
    if (v === null || v <= 0) continue;
    n++;
  }
  return n;
}

/** Pozycje kosztorysu (KNR) z ceną jedn. &gt; 0 — gdy brak wierszy przedmiaru EasyEstimate. */
export function countEstimatePositionsWithPositiveUnitPrice(positions: { unit_price: unknown }[]): number {
  let n = 0;
  for (const p of positions) {
    const v = parseFloat(String(p.unit_price ?? '').replace(/\s/g, '').replace(',', '.'));
    if (Number.isFinite(v) && v > 0) n++;
  }
  return n;
}

export function sumNettoFromEasyPrzedmiarRows(rows: EstimateEasyPrzedmiarRowDto[]): number {
  let sum = 0;
  for (const r of rows) {
    const rt = String(r.rowType || '').toLowerCase();
    if (rt !== 'pozycja') continue;
    const ex = (r.extras && typeof r.extras === 'object' ? r.extras : {}) as Record<string, unknown>;
    let iloscRaw = String(r.ilosc ?? '').trim();
    let cenaRaw = ex.cenaJedn != null ? String(ex.cenaJedn).trim() : '';
    const patch = ex.cellPatch;
    if (patch && typeof patch === 'object') {
      const p = patch as Record<string, unknown>;
      if (typeof p.ilosc === 'string') iloscRaw = p.ilosc.trim();
      if (typeof p.cenaJedn === 'string') cenaRaw = p.cenaJedn.trim();
    }
    if (!iloscRaw || !cenaRaw) continue;
    const iq = parseLoosePlDecimal(iloscRaw);
    const cq = parseLoosePlDecimal(cenaRaw);
    if (iq === null || cq === null) continue;
    sum += Math.round(iq * cq * 100) / 100;
  }
  return Math.round(sum * 100) / 100;
}
